// A tiny shared-request cache for opt-in template data hooks : one
// in-flight request per key, kept for `ttlMs`, dropped on failure. Only
// non-classic templates call these hooks, so classic's request order is
// never affected.

import { useEffect, useState } from 'react'

type Entry<T> = { at: number; promise: Promise<T>; value?: T }
const store = new Map<string, Entry<unknown>>()

export function sharedRequest<T>(key: string, ttlMs: number, run: () => Promise<T>, force = false): Entry<T> {
  const hit = store.get(key) as Entry<T> | undefined
  if (hit && !force && Date.now() - hit.at < ttlMs) return hit
  const entry: Entry<T> = {
    at: Date.now(),
    promise: run().then(
      (v) => { entry.value = v; return v },
      (e) => { if (store.get(key) === entry) store.delete(key); throw e },
    ),
  }
  store.set(key, entry as Entry<unknown>)
  return entry
}

/** Test hook. */
export function resetSharedRequests(): void {
  store.clear()
}

export type SharedState<T> = { status: 'idle' | 'loading' | 'ready' | 'error'; value: T | null }

/** Subscribe a component to a shared request; nothing runs while `key` is null. */
export function useSharedRequest<T>(key: string | null, ttlMs: number, run: () => Promise<T>): SharedState<T> {
  const [state, setState] = useState<{ key: string | null; value: T | null; error: boolean }>({ key: null, value: null, error: false })
  useEffect(() => {
    if (!key) return
    let live = true
    const entry = sharedRequest(key, ttlMs, run)
    if (entry.value !== undefined) setState({ key, value: entry.value, error: false })
    else entry.promise.then(
      (value) => { if (live) setState({ key, value, error: false }) },
      () => { if (live) setState({ key, value: null, error: true }) },
    )
    return () => { live = false }
    // `run` is keyed by `key`; callers build it inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ttlMs])
  if (!key) return { status: 'idle', value: null }
  if (state.key !== key) return { status: 'loading', value: null }
  if (state.error) return { status: 'error', value: null }
  return state.value === null ? { status: 'loading', value: null } : { status: 'ready', value: state.value }
}
